import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  EmptyState,
  ErrorBanner,
  FrmIcon,
  Pill,
  ProgressBar,
  RoadmapTaskList,
  SearchInput,
  SegmentedControl,
  StatusPill,
  Surface,
  Textarea,
  WorkbenchPanel,
  useToast,
} from '@frm/ui'

import { fetchAgentsHealth, type AgentHealth } from '../api/agents'
import {
  cancelChatRun,
  chatStreamUrl,
  createChatSession,
  fetchChatConversations,
  fetchChatMessages,
  sendChatCommand,
  sendChatMessage,
  type ChatConversationDto,
  type ChatMessageDto,
  type ChatSessionDto,
  type ChatStreamEvent,
} from '../api/chat'
import { ApiError } from '../api/client'
import { fetchReleases, type ReleaseSprintDto, type RoadmapTaskState } from '../api/releases'
import { formatAge, MONO_NUMERIC_STYLE } from '../shared/format'

type ChatMode = 'chat' | 'plan' | 'act'
type ChatChannelFilter = 'all' | 'telegram' | 'web'

interface ChatAgent {
  id: string
  label: string
  username: string | null
  role: string
  engine: string
  model: string
  state: AgentHealth['state'] | 'idle'
  project: string
  sprint: string
  lastActivityAt: string | null
}

interface ChatDialogue {
  id: string
  sessionId: string | null
  channel: 'telegram' | 'web'
  name: string
  handle: string
  initials: string
  agentId: string
  project: string
  sprint: string | null
  engine: string
  model: string
  preview: string
  time: string | null
  unread: number
  running: boolean
  state: ChatAgent['state']
  virtual?: boolean
}

interface ActiveSprint {
  release: string
  sprint: ReleaseSprintDto
}

const PAGE_STYLE: CSSProperties = {
  minWidth: 0,
}

const STACK_STYLE: CSSProperties = {
  display: 'grid',
  gap: 12,
  minWidth: 0,
}

const ROW_STYLE: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  minWidth: 0,
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted, #94a3b8)',
}

const WELL_STYLE: CSSProperties = {
  background: 'var(--frm-workbench-well, #0B1020)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 12,
}

const MODE_ITEMS = [
  { id: 'chat', label: 'chat' },
  { id: 'plan', label: 'plan' },
  { id: 'act', label: 'act' },
] satisfies Array<{ id: ChatMode; label: string }>

const CHANNEL_ITEMS = [
  { id: 'all', label: 'Все' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'web', label: 'Web' },
] satisfies Array<{ id: ChatChannelFilter; label: string }>

const COMMAND_DEFS = [
  { id: 'checkpoint', label: '/checkpoint', cmd: '/checkpoint' },
  { id: 'mode', label: '/mode', cmd: '/mode', modeArg: true },
  { id: 'plan', label: '/plan', cmd: '/plan' },
  { id: 'status', label: '/status', cmd: '/status' },
  { id: 'project', label: '/project', cmd: '/project' },
  { id: 'sprint', label: '/sprint', cmd: '/sprint' },
  { id: 'vault', label: '/vault', cmd: '/vault' },
  { id: 'skill', label: '/skill', cmd: '/skill' },
  { id: 'release', label: '/release', cmd: '/release', disabledReason: 'owner confirm flow' },
  { id: 'restart', label: '/restart', cmd: '/restart', disabledReason: 'owner confirm flow' },
]

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${error.code ?? error.status}: ${error.message}`
  if (error instanceof Error) return error.message
  return String(error)
}

function normalizeMode(value: string | null | undefined): ChatMode {
  return value === 'plan' || value === 'act' ? value : 'chat'
}

function statusKind(state: ChatAgent['state']) {
  switch (state) {
    case 'active':
      return 'ok' as const
    case 'idle':
      return 'unknown' as const
    case 'stale':
      return 'warning' as const
    case 'error':
      return 'danger' as const
  }
}

function messageTone(role: ChatMessageDto['role']) {
  switch (role) {
    case 'user':
      return 'info' as const
    case 'agent':
      return 'success' as const
    case 'tool':
      return 'warning' as const
    case 'system':
      return 'neutral' as const
  }
}

function initialsOf(value: string | null | undefined): string {
  const parts = (value || 'AI')
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .slice(0, 2)
  const initials = parts.map(part => part[0]?.toUpperCase()).join('')
  return initials || 'AI'
}

function agentFromHealth(agent: AgentHealth): ChatAgent {
  return {
    id: agent.name,
    label: agent.name,
    username: agent.tgUsername,
    role: agent.role,
    engine: agent.engine,
    model: agent.model,
    state: agent.state,
    project: agent.project,
    sprint: agent.sprint,
    lastActivityAt: agent.raw.lastActivityAt,
  }
}

function fallbackAgent(selectedAgentId?: string): ChatAgent {
  const id = selectedAgentId || 'codex-develop'
  return {
    id,
    label: id,
    username: null,
    role: 'develop',
    engine: 'codex',
    model: 'unknown',
    state: 'idle',
    project: 'frm.ai/ai.agents',
    sprint: 'ai-agents-new-frontend',
    lastActivityAt: null,
  }
}

function normalizeChatScopeSprint(sprint: string | null | undefined): string {
  return sprint?.trim() || ''
}

function sameChatScope(
  leftProject: string,
  leftSprint: string | null | undefined,
  rightProject: string,
  rightSprint: string | null | undefined,
): boolean {
  return leftProject === rightProject && normalizeChatScopeSprint(leftSprint) === normalizeChatScopeSprint(rightSprint)
}

function conversationMatchesAgentScope(conversation: Pick<ChatConversationDto, 'agent' | 'project' | 'sprint'>, agent: ChatAgent): boolean {
  return conversation.agent === agent.id && sameChatScope(conversation.project, conversation.sprint, agent.project, agent.sprint)
}

function sessionMatchesAgentScope(session: Pick<ChatSessionDto, 'agent' | 'project' | 'sprint'>, agent: ChatAgent): boolean {
  return session.agent === agent.id && sameChatScope(session.project, session.sprint, agent.project, agent.sprint)
}

function latestConversationFor(agent: ChatAgent, conversations: ChatConversationDto[] | undefined): ChatConversationDto | null {
  return conversations?.find(conversation => conversationMatchesAgentScope(conversation, agent)) ?? null
}

function dialogueFromConversation(conversation: ChatConversationDto, agent: ChatAgent | undefined): ChatDialogue {
  return {
    id: conversation.id,
    sessionId: conversation.id,
    channel: conversation.channel,
    name: conversation.name || conversation.agent,
    handle: conversation.handle,
    initials: conversation.initials || initialsOf(conversation.agent),
    agentId: conversation.agent,
    project: conversation.project,
    sprint: conversation.sprint,
    engine: conversation.engine,
    model: conversation.model,
    preview: conversation.preview,
    time: conversation.time,
    unread: conversation.unread,
    running: conversation.running,
    state: agent?.state ?? (conversation.running ? 'active' : 'idle'),
  }
}

function virtualDialogue(agent: ChatAgent): ChatDialogue {
  return {
    id: `agent:${agent.id}`,
    sessionId: null,
    channel: 'web',
    name: agent.label,
    handle: agent.username ? `@${agent.username}` : `web:${agent.id}`,
    initials: initialsOf(agent.label),
    agentId: agent.id,
    project: agent.project,
    sprint: agent.sprint,
    engine: agent.engine,
    model: agent.model,
    preview: 'Новая web-сессия',
    time: agent.lastActivityAt,
    unread: 0,
    running: false,
    state: agent.state,
    virtual: true,
  }
}

function dialogueTimeMs(dialogue: ChatDialogue): number {
  if (!dialogue.time) return 0
  const ms = new Date(dialogue.time).getTime()
  return Number.isNaN(ms) ? 0 : ms
}

function buildDialogues(agents: ChatAgent[], conversations: ChatConversationDto[] | undefined): ChatDialogue[] {
  const byAgent = new Map(agents.map(agent => [agent.id, agent]))
  const realDialogues = (conversations ?? []).map(conversation => dialogueFromConversation(conversation, byAgent.get(conversation.agent)))
  const hasConversation = new Set(realDialogues.map(dialogue => dialogue.agentId))
  const virtualDialogues = agents
    .filter(agent => !hasConversation.has(agent.id))
    .map(virtualDialogue)
  return [...realDialogues, ...virtualDialogues].sort((a, b) => {
    if (a.virtual !== b.virtual) return a.virtual ? 1 : -1
    return dialogueTimeMs(b) - dialogueTimeMs(a)
  })
}

function dialogueMatches(dialogue: ChatDialogue, search: string, channel: ChatChannelFilter): boolean {
  if (channel !== 'all' && dialogue.channel !== channel) return false
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [
    dialogue.name,
    dialogue.handle,
    dialogue.agentId,
    dialogue.project,
    dialogue.sprint ?? '',
    dialogue.engine,
    dialogue.model,
    dialogue.preview,
  ].some(value => value.toLowerCase().includes(needle))
}

function sprintMatches(candidateId: string, target: string | null | undefined): boolean {
  if (!target) return false
  if (candidateId === target) return true
  const candidateTail = candidateId.split('/').filter(Boolean).at(-1)
  const targetTail = target.split('/').filter(Boolean).at(-1)
  return Boolean(candidateTail && targetTail && candidateTail === targetTail)
}

function activeSprintFromReleases(releases: Awaited<ReturnType<typeof fetchReleases>> | undefined, target: string | null | undefined): ActiveSprint | null {
  const sprints = (releases ?? []).flatMap(release => release.sprints.map(sprint => ({ release: release.v, sprint })))
  return sprints.find(item => sprintMatches(item.sprint.id, target)) ?? sprints.find(item => item.sprint.state === 'active') ?? null
}

function roadmapStateLabel(state: RoadmapTaskState): string {
  switch (state) {
    case 'x':
      return 'done'
    case '~':
      return 'in progress'
    case '!':
      return 'blocked'
    default:
      return 'todo'
  }
}

function toolMeta(toolName: string | undefined, text: string) {
  const raw = `${toolName ?? ''} ${text}`.toLowerCase()
  if (raw.includes('grep') || raw.includes('search')) return { name: 'Grep', tone: 'info' as const, color: 'var(--frm-info, #38BDF8)' }
  if (raw.includes('bash') || raw.includes('shell') || raw.includes('exec')) return { name: 'Bash', tone: 'success' as const, color: 'var(--frm-success, #34D399)' }
  if (raw.includes('edit') || raw.includes('write') || raw.includes('patch')) return { name: 'Edit', tone: 'warning' as const, color: 'var(--frm-warning, #FBBF24)' }
  return { name: toolName || 'Read', tone: 'info' as const, color: 'var(--frm-info, #38BDF8)' }
}

function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  )
}

function DialogueButton({
  dialogue,
  selected,
  onSelect,
}: {
  dialogue: ChatDialogue
  selected: boolean
  onSelect: (dialogue: ChatDialogue) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(dialogue)}
      aria-current={selected ? 'page' : undefined}
      style={{
        ...WELL_STYLE,
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        gap: 10,
        alignItems: 'center',
        padding: 10,
        borderColor: selected ? 'rgba(56, 189, 248, 0.52)' : 'rgba(148, 163, 184, 0.14)',
        background: selected ? 'rgba(56, 189, 248, 0.08)' : 'var(--frm-workbench-well, #0B1020)',
        color: 'inherit',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ position: 'relative' }}>
        <Avatar name={dialogue.name} shape="square" size="md" status={selected || dialogue.running ? 'online' : 'offline'} style={{ flexShrink: 0 }} />
        <span
          aria-label={dialogue.channel}
          title={dialogue.channel}
          style={{
            position: 'absolute',
            right: -3,
            bottom: -3,
            width: 16,
            height: 16,
            display: 'inline-grid',
            placeItems: 'center',
            borderRadius: 999,
            border: '1px solid var(--frm-surface, #101423)',
            background: dialogue.channel === 'web' ? 'var(--frm-accent, #38BDF8)' : '#36AEE2',
            color: '#04111D',
            fontSize: 9,
            fontWeight: 900,
          }}
        >
          {dialogue.channel === 'web' ? 'W' : 'T'}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
        <div style={{ ...ROW_STYLE, gap: 8 }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 800 }}>
            {dialogue.name}
          </span>
          <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 10.5, flexShrink: 0 }}>
            {dialogue.time ? formatAge(dialogue.time) : 'new'}
          </code>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              flexShrink: 0,
              background: dialogue.running ? 'var(--frm-success, #34D399)' : 'var(--frm-text-muted, #64748B)',
              animation: dialogue.running ? 'frm-pulse 1.6s ease-in-out infinite' : undefined,
            }}
          />
          <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-2)', fontSize: 10.5, flexShrink: 0 }}>
            {dialogue.agentId}
          </code>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--frm-text-3)', fontSize: 11.5 }}>
            {dialogue.preview}
          </span>
        </div>
        <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {dialogue.project} · {dialogue.sprint ?? 'no sprint'}
        </code>
      </div>
      <div style={{ display: 'grid', justifyItems: 'end', gap: 6 }}>
        {dialogue.unread ? <Pill tone="info" size="sm">{dialogue.unread}</Pill> : null}
        <StatusPill status={dialogue.running ? 'warning' : statusKind(dialogue.state)} label={dialogue.running ? 'running' : dialogue.virtual ? 'new' : dialogue.state} />
      </div>
    </button>
  )
}

function ToolCallCard({
  message,
  open,
  onToggle,
}: {
  message: ChatMessageDto
  open: boolean
  onToggle: () => void
}) {
  const meta = toolMeta(message.toolName, message.text)
  const lines = message.text.split('\n').slice(0, open ? 80 : 1)
  return (
    <div
      style={{
        marginLeft: 42,
        border: `1px solid ${meta.color}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--frm-workbench-well, #0B1020)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'auto auto minmax(0, 1fr) auto auto',
          gap: 10,
          alignItems: 'center',
          padding: '11px 13px',
          border: 0,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            width: 25,
            height: 25,
            display: 'inline-grid',
            placeItems: 'center',
            borderRadius: 8,
            background: `${meta.color}1f`,
            color: meta.color,
            fontFamily: 'var(--frm-font-mono, JetBrains Mono, monospace)',
            fontWeight: 900,
          }}
        >
          {meta.name[0]}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 850 }}>{meta.name}</span>
        <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5 }}>
          {message.toolName || message.author}
        </code>
        <Pill tone={message.status === 'blocked' ? 'danger' : meta.tone} size="sm">{message.status ?? 'trace'}</Pill>
        <span aria-hidden style={{ color: 'var(--frm-text-3)', transform: open ? 'rotate(90deg)' : undefined }}>›</span>
      </button>
      {open ? (
        <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.12)', background: 'var(--frm-workbench-well-2, #080C16)', padding: '11px 14px', overflowX: 'auto' }}>
          {lines.map((line, index) => (
            <code key={`${message.id}-${index}`} style={{ ...MONO_NUMERIC_STYLE, display: 'block', color: 'var(--frm-text-2)', fontSize: 11.5, lineHeight: 1.65, whiteSpace: 'pre' }}>
              {line || ' '}
            </code>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function MessageItem({
  message,
  agent,
  toolOpen,
  onToggleTool,
}: {
  message: ChatMessageDto
  agent: ChatAgent
  toolOpen: boolean
  onToggleTool: () => void
}) {
  if (message.role === 'tool' || message.toolName) {
    return <ToolCallCard message={message} open={toolOpen} onToggle={onToggleTool} />
  }

  if (message.role === 'system') {
    const blocked = message.status === 'blocked' || /прерван|timeout|ошиб/i.test(message.text)
    return (
      <div
        style={{
          alignSelf: 'center',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          maxWidth: 'min(720px, 100%)',
          padding: '8px 12px',
          border: `1px solid ${blocked ? 'rgba(251, 191, 36, 0.28)' : 'rgba(148, 163, 184, 0.18)'}`,
          borderRadius: 999,
          background: blocked ? 'rgba(251, 191, 36, 0.08)' : 'rgba(148, 163, 184, 0.07)',
          color: blocked ? 'var(--frm-warning, #FBBF24)' : 'var(--frm-text-3)',
          fontSize: 12.5,
          fontWeight: 700,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor', flexShrink: 0 }} />
        <span style={{ overflowWrap: 'anywhere' }}>{message.text}</span>
      </div>
    )
  }

  const isUser = message.role === 'user'
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        gap: 11,
        animation: 'msg-in .24s cubic-bezier(.2,0,0,1)',
      }}
    >
      {!isUser ? <Avatar name={agent.label} shape="square" size="md" status={agent.state === 'active' ? 'online' : 'offline'} style={{ flexShrink: 0 }} /> : null}
      <div style={{ display: 'grid', gap: 6, justifyItems: isUser ? 'end' : 'start', maxWidth: 'min(760px, 88%)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
          <span style={{ color: 'var(--frm-text-2)', fontSize: 12, fontWeight: 760 }}>{message.author}</span>
          <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 10.5 }}>
            {formatAge(message.time)}
          </code>
          {message.status ? <Pill tone={message.status === 'blocked' ? 'danger' : messageTone(message.role)} size="sm">{message.status}</Pill> : null}
        </div>
        <div
          style={{
            padding: '11px 15px',
            borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            border: isUser ? '1px solid rgba(56, 189, 248, 0.28)' : '1px solid rgba(148, 163, 184, 0.16)',
            background: isUser
              ? 'linear-gradient(135deg, rgba(56,189,248,0.16), rgba(99,102,241,0.16))'
              : 'var(--frm-surface, #101423)',
            color: 'var(--frm-text, #F8FAFC)',
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {message.text}
        </div>
      </div>
    </div>
  )
}

function RunProgressCard({ runId }: { runId: string | null }) {
  return (
    <div
      style={{
        marginLeft: 42,
        display: 'grid',
        gap: 12,
        padding: 16,
        border: '1px solid rgba(56, 189, 248, 0.30)',
        borderRadius: 14,
        background: 'linear-gradient(180deg, rgba(56,189,248,0.06), rgba(11,16,32,0.6)), #0B1020',
        boxShadow: '0 14px 40px rgba(0,0,0,0.28)',
      }}
    >
      <div style={ROW_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 999,
              border: '2px solid rgba(56, 189, 248, 0.30)',
              borderTopColor: 'var(--frm-accent, #38BDF8)',
              animation: 'frm-spin 900ms linear infinite',
            }}
          />
          <strong>Engine run выполняется</strong>
        </div>
        <Pill tone="warning">SSE</Pill>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 10, background: 'var(--frm-workbench-well-2, #080C16)', border: '1px solid rgba(148, 163, 184, 0.12)' }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--frm-accent, #38BDF8)', animation: 'frm-pulse 1.2s ease-in-out infinite', flexShrink: 0 }} />
        <span style={{ color: 'var(--frm-text-2)', fontSize: 12.5 }}>Сейчас: ответ движка будет добавлен в поток после события `agent_text`.</span>
        {runId ? <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', marginLeft: 'auto', fontSize: 11 }}>{runId.slice(0, 8)}</code> : null}
      </div>
    </div>
  )
}

function ChatHeader({
  agent,
  session,
  mode,
  isRunning,
  canCommand,
  cancelPending,
  onModeChange,
  onCancel,
}: {
  agent: ChatAgent
  session: ChatSessionDto | null
  mode: ChatMode
  isRunning: boolean
  canCommand: boolean
  cancelPending: boolean
  onModeChange: (mode: ChatMode) => void
  onCancel: () => void
}) {
  return (
    <div className="chat-location-header">
      <Avatar name={agent.label} shape="square" size="md" status={agent.state === 'active' || isRunning ? 'online' : 'offline'} style={{ flexShrink: 0 }} />
      <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--frm-text)', fontSize: 15, fontWeight: 850 }}>{agent.label}</span>
          <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 11.5 }}>
            {agent.username ? `@${agent.username}` : `web:${agent.id}`}
          </code>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, overflowX: 'auto' }}>
          <Pill tone="info" size="sm">{session?.project || agent.project}</Pill>
          <Pill tone="neutral" size="sm">{session?.sprint || agent.sprint || 'no sprint'}</Pill>
          <Pill tone="neutral" size="sm">{session?.engine || agent.engine}</Pill>
          <Pill tone="neutral" size="sm">{session?.model || agent.model}</Pill>
          <Pill tone={isRunning ? 'warning' : session?.status === 'error' ? 'danger' : 'success'} size="sm">
            {isRunning ? 'running' : session?.status || 'idle'}
          </Pill>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <SegmentedControl<ChatMode>
          ariaLabel="Режим web-чата"
          items={MODE_ITEMS}
          value={mode}
          onChange={onModeChange}
          disabled={!canCommand}
          size="sm"
        />
      </div>
      {isRunning ? (
        <Button
          className="chat-floating-cancel"
          disabled={cancelPending}
          loading={cancelPending}
          onClick={onCancel}
          size="sm"
          variant="danger"
        >
          <StopIcon /> Прервать (Esc)
        </Button>
      ) : null}
    </div>
  )
}

function SprintPanel({
  activeSprint,
  targetSprint,
  loading,
  error,
  running,
  commandPending,
  onLaunchNext,
}: {
  activeSprint: ActiveSprint | null
  targetSprint: string | null
  loading: boolean
  error: unknown
  running: boolean
  commandPending: boolean
  onLaunchNext: (taskText: string) => void
}) {
  if (error) {
    return (
      <ErrorBanner
        title="Спринт не загружен"
        description={errorMessage(error)}
      />
    )
  }

  if (loading) {
    return (
      <Surface padding="md" variant="subtle">
        <span style={MUTED_STYLE}>Загружаем roadmap активного спринта...</span>
      </Surface>
    )
  }

  if (!activeSprint) {
    return (
      <EmptyState
        title="Активный спринт не найден"
        description={targetSprint ? `Для ${targetSprint} нет read-only данных в /api/releases.` : 'У выбранной сессии не указан sprint.'}
      />
    )
  }

  const { sprint, release } = activeSprint
  const progress = sprint.total ? Math.round((sprint.done / sprint.total) * 100) : 0
  const nextTask = sprint.tasks.find(task => task.st === ' ') ?? null
  return (
    <div style={STACK_STYLE}>
      <Surface padding="md" variant="subtle" style={{ borderColor: 'rgba(56, 189, 248, 0.22)' }}>
        <div style={STACK_STYLE}>
          <div style={ROW_STYLE}>
            <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <span style={{ color: 'var(--frm-text)', fontSize: 14, fontWeight: 850 }}>Активный спринт</span>
              <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 12 }}>
                {sprint.id}
              </code>
            </div>
            <Pill tone={sprint.state === 'active' ? 'info' : sprint.state === 'closed' ? 'success' : 'neutral'}>{sprint.state}</Pill>
          </div>
          <span style={{ color: 'var(--frm-text-3)', fontSize: 12.5, lineHeight: 1.5 }}>{sprint.goal}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ProgressBar
              value={sprint.done}
              max={sprint.total}
              tone="accent"
              size="md"
              style={{ flex: 1 }}
              ariaLabel={`Прогресс спринта ${sprint.id}`}
            />
            <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-2)', fontSize: 12 }}>{sprint.done}/{sprint.total}</code>
          </div>
          <div style={ROW_STYLE}>
            <Pill tone="neutral" size="sm">release {release}</Pill>
            <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 11 }}>{progress}%</code>
          </div>
        </div>
      </Surface>

      <RoadmapTaskList
        tasks={sprint.tasks.slice(0, 8).map((task, index) => ({
          id: `${sprint.id}-${index}`,
          state: task.st,
          text: task.text,
          criteria: task.note,
          meta: roadmapStateLabel(task.st),
        }))}
        density="compact"
        showCriteria
        emptyText="В roadmap нет задач"
      />

      <Button
        disabled={!nextTask || running || commandPending}
        loading={commandPending}
        onClick={() => nextTask && onLaunchNext(nextTask.text)}
        variant="primary"
      >
        Запустить следующую задачу
      </Button>
      <span style={{ color: 'var(--frm-text-3)', fontSize: 11.5, lineHeight: 1.45, textAlign: 'center' }}>
        {nextTask ? `следующая: ${nextTask.text}` : 'все задачи спринта закрыты или не найдены'}
      </span>
    </div>
  )
}

export function ChatWorkbench({ selectedAgentId }: { selectedAgentId?: string }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [draft, setDraft] = useState('')
  const [activeSession, setActiveSession] = useState<ChatSessionDto | null>(null)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [dialogueSearch, setDialogueSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState<ChatChannelFilter>('all')
  const [localMode, setLocalMode] = useState<ChatMode>('chat')
  const [openTools, setOpenTools] = useState<Record<string, boolean>>({})
  const messageEndRef = useRef<HTMLDivElement | null>(null)

  const agentsQuery = useQuery({
    queryKey: ['agents', 'health'],
    queryFn: fetchAgentsHealth,
    refetchInterval: 30_000,
  })
  const conversationsQuery = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: fetchChatConversations,
    refetchInterval: activeRunId ? 2_000 : 15_000,
  })
  const releasesQuery = useQuery({
    queryKey: ['releases'],
    queryFn: fetchReleases,
    staleTime: 60_000,
  })

  const agents = useMemo(() => {
    const fromHealth = agentsQuery.data?.agents.map(agentFromHealth) ?? []
    if (fromHealth.length) return fromHealth
    return [fallbackAgent(selectedAgentId)]
  }, [agentsQuery.data?.agents, selectedAgentId])

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) ?? agents[0] ?? fallbackAgent(selectedAgentId),
    [agents, selectedAgentId],
  )

  const requestedSessionId = searchParams.get('session')
  const requestedConversation = useMemo(
    () => conversationsQuery.data?.find((conversation) => conversation.agent === selectedAgent.id && conversation.id === requestedSessionId) ?? null,
    [conversationsQuery.data, requestedSessionId, selectedAgent.id],
  )
  const latestConversation = requestedConversation ?? latestConversationFor(selectedAgent, conversationsQuery.data)
  const scopedActiveSession = useMemo(() => {
    if (!activeSession) return null
    if (requestedConversation && activeSession.id === requestedConversation.id) return activeSession
    return sessionMatchesAgentScope(activeSession, selectedAgent) ? activeSession : null
  }, [activeSession, requestedConversation, selectedAgent])
  const sessionId = scopedActiveSession?.id ?? requestedSessionId ?? latestConversation?.id ?? null

  const dialogues = useMemo(() => buildDialogues(agents, conversationsQuery.data), [agents, conversationsQuery.data])
  const visibleDialogues = useMemo(
    () => dialogues.filter(dialogue => dialogueMatches(dialogue, dialogueSearch, channelFilter)),
    [channelFilter, dialogueSearch, dialogues],
  )
  const selectedDialogueId = sessionId ?? `agent:${selectedAgent.id}`

  useEffect(() => {
    const conversation = requestedConversation ?? latestConversationFor(selectedAgent, conversationsQuery.data)
    if (conversation && (!scopedActiveSession || scopedActiveSession.agent !== selectedAgent.id || scopedActiveSession.id !== conversation.id)) {
      setActiveSession({
        id: conversation.id,
        agent: conversation.agent,
        project: conversation.project,
        sprint: conversation.sprint,
        engine: conversation.engine,
        model: conversation.model,
        mode: localMode,
        status: conversation.running ? 'running' : 'idle',
        startedAt: conversation.time,
        lastActivityAt: conversation.time,
        runId: undefined,
      })
      return
    }
    if (requestedSessionId && (!scopedActiveSession || scopedActiveSession.agent !== selectedAgent.id || scopedActiveSession.id !== requestedSessionId)) {
      setActiveSession({
        id: requestedSessionId,
        agent: selectedAgent.id,
        project: selectedAgent.project,
        sprint: selectedAgent.sprint,
        engine: selectedAgent.engine,
        model: selectedAgent.model,
        mode: localMode,
        status: 'idle',
        startedAt: new Date().toISOString(),
        lastActivityAt: new Date().toISOString(),
      })
      return
    }
    if (activeSession && !scopedActiveSession) {
      setActiveSession(null)
      setActiveRunId(null)
    }
  }, [activeSession, conversationsQuery.data, localMode, requestedConversation, requestedSessionId, scopedActiveSession, selectedAgent])

  useEffect(() => {
    setLocalMode(normalizeMode(scopedActiveSession?.mode))
  }, [scopedActiveSession?.id, scopedActiveSession?.mode])

  const messagesQuery = useQuery({
    queryKey: ['chat', selectedAgent.id, sessionId, 'messages'],
    queryFn: () => fetchChatMessages(selectedAgent.id, sessionId),
    enabled: Boolean(sessionId),
    refetchInterval: activeRunId ? 2_000 : false,
  })

  const createSessionMutation = useMutation({
    mutationFn: () => createChatSession({
      agent: selectedAgent.id,
      project: selectedAgent.project,
      sprint: selectedAgent.sprint,
      mode: localMode,
    }),
    onSuccess: (session) => {
      setActiveSession(session)
      setLocalMode(normalizeMode(session.mode))
      navigate(`/chat/${session.agent}?session=${encodeURIComponent(session.id)}`)
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
    },
    onError: (error) => toast({
      title: 'Сессия не создана',
      description: errorMessage(error),
      tone: 'danger',
    }),
  })

  const ensureSession = useCallback(async (): Promise<ChatSessionDto> => {
    if (sessionId) {
      return scopedActiveSession ?? {
        id: sessionId,
        agent: selectedAgent.id,
        project: selectedAgent.project,
        sprint: selectedAgent.sprint,
        engine: selectedAgent.engine,
        model: selectedAgent.model,
        mode: localMode,
        status: 'idle',
        startedAt: new Date().toISOString(),
        lastActivityAt: new Date().toISOString(),
      }
    }
    return createSessionMutation.mutateAsync()
  }, [
    createSessionMutation,
    localMode,
    scopedActiveSession,
    selectedAgent.engine,
    selectedAgent.id,
    selectedAgent.model,
    selectedAgent.project,
    selectedAgent.sprint,
    sessionId,
  ])

  const sendMutation = useMutation({
    mutationFn: async (text: string) => {
      const session = await ensureSession()
      return sendChatMessage(selectedAgent.id, session.id, text)
    },
    onSuccess: (dto) => {
      setDraft('')
      setActiveSession(dto.session)
      setActiveRunId(dto.runId)
      setLocalMode(normalizeMode(dto.session.mode))
      navigate(`/chat/${dto.session.agent}?session=${encodeURIComponent(dto.session.id)}`)
      void queryClient.invalidateQueries({ queryKey: ['chat', selectedAgent.id, dto.session.id, 'messages'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      toast({
        title: 'Сообщение отправлено',
        description: 'Web-run запущен, ответ придёт через SSE.',
        tone: 'success',
      })
    },
    onError: (error) => toast({
      title: 'Сообщение не отправлено',
      description: errorMessage(error),
      tone: 'danger',
    }),
  })

  const commandMutation = useMutation({
    mutationFn: async ({ cmd, args = '' }: { cmd: string; args?: string }) => {
      const session = await ensureSession()
      return sendChatCommand(selectedAgent.id, session.id, cmd, args)
    },
    onSuccess: (dto) => {
      setActiveSession(dto.session)
      setActiveRunId(dto.runId)
      setLocalMode(normalizeMode(dto.session.mode))
      navigate(`/chat/${dto.session.agent}?session=${encodeURIComponent(dto.session.id)}`)
      void queryClient.invalidateQueries({ queryKey: ['chat', selectedAgent.id, dto.session.id, 'messages'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      toast({
        title: 'Команда отправлена',
        description: 'Web-command запущена через live endpoint.',
        tone: 'success',
      })
    },
    onError: (error) => toast({
      title: 'Команда не отправлена',
      description: errorMessage(error),
      tone: 'danger',
    }),
  })

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId) throw new Error('Нет активной web-сессии')
      return cancelChatRun(selectedAgent.id, sessionId)
    },
    onSuccess: (session) => {
      setActiveSession(session)
      setActiveRunId(null)
      void queryClient.invalidateQueries({ queryKey: ['chat', selectedAgent.id, session.id, 'messages'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      toast({
        title: 'Run прерван',
        description: 'AbortSignal отправлен активному web-run.',
        tone: 'warning',
      })
    },
    onError: (error) => toast({
      title: 'Cancel не выполнен',
      description: errorMessage(error),
      tone: 'danger',
    }),
  })

  const messages = messagesQuery.data ?? []
  const isRunning = Boolean(activeRunId) || scopedActiveSession?.status === 'running' || Boolean(latestConversation?.running)
  const canSend = Boolean(draft.trim()) && !sendMutation.isPending && !isRunning
  const canCommand = !commandMutation.isPending && !sendMutation.isPending && !isRunning
  const targetSprint = scopedActiveSession?.sprint ?? selectedAgent.sprint ?? null
  const activeSprint = useMemo(() => activeSprintFromReleases(releasesQuery.data, targetSprint), [releasesQuery.data, targetSprint])

  useEffect(() => {
    if (!sessionId || !activeRunId) return undefined
    const stream = new EventSource(chatStreamUrl(selectedAgent.id, sessionId, activeRunId))
    const finish = (session?: ChatSessionDto) => {
      if (session) {
        setActiveSession(session)
        setLocalMode(normalizeMode(session.mode))
      }
      setActiveRunId(null)
      void queryClient.invalidateQueries({ queryKey: ['chat', selectedAgent.id, sessionId, 'messages'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      stream.close()
    }
    const handle = (event: MessageEvent<string>) => {
      const payload = JSON.parse(event.data) as ChatStreamEvent
      if (payload.type === 'done' || payload.type === 'aborted') finish(payload.session)
      if (payload.type === 'error') {
        toast({ title: 'Web-run завершился с ошибкой', description: payload.message, tone: 'danger' })
        finish(payload.session)
      }
      if (payload.type === 'agent_text') {
        void queryClient.invalidateQueries({ queryKey: ['chat', selectedAgent.id, sessionId, 'messages'] })
      }
    }
    stream.addEventListener('ready', handle as EventListener)
    stream.addEventListener('agent_text', handle as EventListener)
    stream.addEventListener('done', handle as EventListener)
    stream.addEventListener('error', handle as EventListener)
    stream.addEventListener('aborted', handle as EventListener)
    stream.onerror = () => {
      finish()
    }
    return () => {
      stream.close()
    }
  }, [activeRunId, queryClient, selectedAgent.id, sessionId, toast])

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ block: 'end' })
  }, [activeRunId, isRunning, messages.length])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !isRunning || cancelMutation.isPending) return
      event.preventDefault()
      cancelMutation.mutate()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [cancelMutation, isRunning])

  const selectDialogue = useCallback((dialogue: ChatDialogue) => {
    if (dialogue.sessionId) {
      navigate(`/chat/${dialogue.agentId}?session=${encodeURIComponent(dialogue.sessionId)}`)
      return
    }
    navigate(`/chat/${dialogue.agentId}`)
  }, [navigate])

  const sendCommand = useCallback((cmd: string, args = '') => {
    commandMutation.mutate({ cmd, args })
  }, [commandMutation])

  const handleModeChange = useCallback((mode: ChatMode) => {
    if (!canCommand) return
    setLocalMode(mode)
    sendCommand('/mode', mode)
  }, [canCommand, sendCommand])

  const startNextTask = useCallback((taskText: string) => {
    sendCommand('/plan', `следующая задача спринта ${targetSprint ?? ''}: ${taskText}`.trim())
  }, [sendCommand, targetSprint])

  return (
    <div className="chat-workbench-page" style={PAGE_STYLE}>
      {agentsQuery.error ? (
        <ErrorBanner
          title="Не удалось загрузить health агентов"
          description={errorMessage(agentsQuery.error)}
          action={<Button onClick={() => void agentsQuery.refetch()}>Повторить</Button>}
        />
      ) : null}

      <div className="chat-workbench-layout">
        <aside className="chat-dialogues-panel">
          <WorkbenchPanel
            title="Диалоги"
            description="Web sessions владельца"
            actions={<Pill tone={isRunning ? 'warning' : 'success'}>{isRunning ? 'running' : `${conversationsQuery.data?.length ?? 0} live`}</Pill>}
          >
            <div style={STACK_STYLE}>
              <SearchInput
                aria-label="Поиск по диалогам"
                controlSize="sm"
                onChange={(event) => setDialogueSearch(event.currentTarget.value)}
                placeholder="Поиск по диалогам"
                value={dialogueSearch}
              />
              <SegmentedControl<ChatChannelFilter>
                ariaLabel="Фильтр каналов"
                items={CHANNEL_ITEMS}
                onChange={setChannelFilter}
                size="sm"
                value={channelFilter}
                fullWidth
              />
              <Button
                disabled={createSessionMutation.isPending || isRunning}
                loading={createSessionMutation.isPending}
                onClick={() => createSessionMutation.mutate()}
                size="sm"
                variant="secondary"
              >
                Новый web-чат
              </Button>
              <div className="chat-dialogue-list">
                {visibleDialogues.length ? visibleDialogues.map(dialogue => (
                  <DialogueButton
                    key={dialogue.id}
                    dialogue={dialogue}
                    selected={selectedDialogueId === dialogue.id || (!sessionId && dialogue.agentId === selectedAgent.id)}
                    onSelect={selectDialogue}
                  />
                )) : (
                  <EmptyState
                    title={channelFilter === 'telegram' ? 'Telegram history не подключена' : 'Диалоги не найдены'}
                    description={channelFilter === 'telegram' ? 'Текущий web-chat store отдаёт только channel=web; Telegram merge нужен отдельным backend follow-up.' : 'Попробуй изменить поиск или канал.'}
                  />
                )}
              </div>
            </div>
          </WorkbenchPanel>
        </aside>

        <section className="chat-center-panel" aria-label="Поток web-чата">
          <ChatHeader
            agent={selectedAgent}
            session={activeSession}
            mode={localMode}
            isRunning={isRunning}
            canCommand={canCommand}
            cancelPending={cancelMutation.isPending}
            onModeChange={handleModeChange}
            onCancel={() => cancelMutation.mutate()}
          />

          <div className="chat-message-stream">
            <div style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 999, background: 'rgba(148,163,184,0.07)', border: '1px solid rgba(148,163,184,0.12)', color: 'var(--frm-text-3)', fontSize: 11 }}>
              <code style={MONO_NUMERIC_STYLE}>{sessionId ? 'web session' : 'new session'}</code>
            </div>
            {!sessionId ? (
              <Surface padding="md" variant="subtle">
                <span style={MUTED_STYLE}>Выбери диалог или напиши первое сообщение, чтобы создать web-сессию для агента.</span>
              </Surface>
            ) : messagesQuery.isLoading ? (
              <Surface padding="md" variant="subtle">
                <span style={MUTED_STYLE}>Загружаем историю...</span>
              </Surface>
            ) : messages.length ? (
              messages.map((message) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  agent={selectedAgent}
                  toolOpen={openTools[message.id] ?? false}
                  onToggleTool={() => setOpenTools(current => ({ ...current, [message.id]: !(current[message.id] ?? false) }))}
                />
              ))
            ) : (
              <Surface padding="md" variant="subtle">
                <span style={MUTED_STYLE}>История пуста. Отправка первого сообщения запустит engine.</span>
              </Surface>
            )}
            {isRunning ? <RunProgressCard runId={activeRunId ?? activeSession?.runId ?? null} /> : null}
            <div ref={messageEndRef} />
          </div>

          <div className="chat-command-bar">
            <div className="chat-command-chips">
              {COMMAND_DEFS.map((command) => {
                const disabled = Boolean(command.disabledReason) || !canCommand
                return (
                  <Button
                    key={command.id}
                    disabled={disabled}
                    loading={commandMutation.isPending && !command.disabledReason}
                    onClick={() => sendCommand(command.cmd, command.modeArg ? localMode : '')}
                    size="sm"
                    title={command.disabledReason}
                    variant="ghost"
                  >
                    {command.label}
                  </Button>
                )
              })}
            </div>
            <div className="chat-input-row">
              <Textarea
                aria-label="Сообщение агенту"
                autoResize
                controlSize="sm"
                onChange={(event) => setDraft(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSend) {
                    event.preventDefault()
                    sendMutation.mutate(draft)
                  }
                }}
                placeholder="Сообщение агенту или команда /..."
                rows={2}
                value={draft}
                textareaStyle={{
                  minHeight: 48,
                  resize: 'vertical',
                }}
              />
              <Button
                disabled={!canSend}
                loading={sendMutation.isPending || createSessionMutation.isPending}
                onClick={() => sendMutation.mutate(draft)}
                variant="primary"
              >
                <FrmIcon name="send" size={16} /> Отправить
              </Button>
            </div>
          </div>
        </section>

        <aside className="chat-sprint-panel">
          <WorkbenchPanel
            title="Активный спринт"
            description={targetSprint ?? 'no sprint'}
            actions={<Pill tone={activeSprint?.sprint.state === 'active' ? 'info' : 'neutral'}>{activeSprint?.sprint.state ?? 'empty'}</Pill>}
          >
            <SprintPanel
              activeSprint={activeSprint}
              targetSprint={targetSprint}
              loading={releasesQuery.isLoading}
              error={releasesQuery.error}
              running={isRunning}
              commandPending={commandMutation.isPending}
              onLaunchNext={startNextTask}
            />
          </WorkbenchPanel>
        </aside>
      </div>
    </div>
  )
}

export function ChatPage() {
  return <ChatWorkbench />
}
